import type { Filters, ViewTab } from "../types";

import { NO_CAMPAIGN_FILTER_VALUE } from "./queryParams";

/** The Performance Report's back-navigation origin — the same values as the
 * Marketing page's tab ids (deliberately: the `from` param doubles as the
 * destination hash). Any other value is treated as absent. */
export type PerformanceReportOrigin = "referral-links" | "marketing-banner";

export const PERFORMANCE_REPORT_ORIGINS: readonly PerformanceReportOrigin[] = [
  "referral-links",
  "marketing-banner",
];

/** Whitelists a raw `from` param: anything unrecognised — empty, a foreign
 * origin, an open-redirect payload — decodes to `undefined`, and each host
 * renders no back link rather than guessing a destination. */
export const decodeOrigin = (
  value: string | undefined,
): PerformanceReportOrigin | undefined =>
  PERFORMANCE_REPORT_ORIGINS.includes(value as PerformanceReportOrigin)
    ? (value as PerformanceReportOrigin)
    : undefined;

export type BuildPerformanceReportHrefParams = Partial<
  Record<keyof Filters, string | number | null | undefined>
> & {
  /** Seeds the "View by" tab (e.g. "rebate" for a Shared Rebate Programs deep link). */
  tab?: ViewTab;
  /** `YYYY-MM-DD` — pass the caller's own selected range so the linked
   * report opens on the same period instead of its own 30-day default. */
  startDate?: string;
  endDate?: string;
  /** Origin tag for the Performance Report's back-navigation link. */
  from?: PerformanceReportOrigin;
  scaId?: string;
};

/** Builds a Performance Report deep link from filter values. */
export const buildPerformanceReportHref = ({
  scaId,
  ...filters
}: BuildPerformanceReportHrefParams): string => {
  // Encoded, so an id carrying `/` or `..` stays one path segment and cannot
  // turn this relative link into a protocol-relative one pointing off-site.
  const basePath = scaId
    ? `/performance/${encodeURIComponent(scaId)}`
    : "/performance";
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(filters)) {
    if (value === null || value === undefined) continue;
    const stringValue = String(value);
    if (stringValue === "") continue;
    // "source" is the Performance Report's default tab — omit it so callers
    // that pass it explicitly still get the same canonical URL as callers
    // that omit `tab` entirely, matching the round-trip sync's own omission
    // of the default in usePerformanceReportUrlParams.
    if (key === "tab" && stringValue === "source") continue;
    searchParams.set(key, stringValue);
  }

  const query = searchParams.toString();
  return query ? `${basePath}?${query}` : basePath;
};

/** Resolves the `campaign` facet value for a link/banner row's own campaign
 * when building a "View performance" deep link, or undefined when the campaigns
 * flag is off. Off must send nothing: the Performance Report whitelists campaign
 * facet values against its own campaign options, which are empty when the flag
 * is off, so a value there would fail the whitelist and blank the report. A null
 * campaignId maps to the "No campaign" bucket. */
export const campaignDeepLinkValue = (
  campaignsEnabled: boolean,
  campaignId: number | null | undefined,
): string | undefined => {
  if (!campaignsEnabled) return undefined;
  return campaignId != null ? String(campaignId) : NO_CAMPAIGN_FILTER_VALUE;
};
